/**
 * The source page, with optional bounding-box overlay.
 *
 * Boxes from BOTH models arrive normalised 0-1000 (the API converts dots.ocr's
 * pixel coordinates once, server-side), so the same overlay code draws either
 * model at any zoom. That normalisation is the only reason a like-for-like
 * visual comparison is possible at all.
 */

import { useRef, useState } from "react";
import type { Block } from "@/lib/types";
import type { RenderedPage } from "@/lib/pdf";

const COLOURS: Record<string, string> = {
  title: "#7c3aed", "Section-header": "#7c3aed", "Page-header": "#0891b2",
  text: "#2563eb", Text: "#2563eb",
  table: "#dc2626", Table: "#dc2626",
  image: "#ea580c", Picture: "#ea580c", figure: "#ea580c",
  formula: "#059669", Formula: "#059669",
};
const colourFor = (c: string) => COLOURS[c] ?? "#64748b";

export function PageCanvas({ page, overlayBlocks, onFile, pageNum, pageCount, onGoto }: {
  page: RenderedPage | null;
  overlayBlocks: Block[] | null;
  onFile: (f: File) => void;
  pageNum: number;
  pageCount: number;
  onGoto: (n: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  if (!page) {
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f?.type === "application/pdf") onFile(f);
        }}
        onClick={() => inputRef.current?.click()}
        className={`flex h-[420px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition
          ${drag ? "border-fh-navy bg-fh-surface-soft" : "border-fh-border bg-white hover:border-slate-400"}`}
      >
        <p className="text-fh-body font-medium text-fh-text-body">Drop a PDF here</p>
        <p className="mt-1 text-fh-meta text-fh-text-muted">
          Rendered in your browser — only the page image is sent to the models.
        </p>
        <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative overflow-hidden rounded-lg ring-1 ring-fh-border-soft bg-white">
        <img src={`data:image/png;base64,${page.base64}`} alt={`page ${pageNum}`} className="block w-full" />
        {overlayBlocks && (
          // viewBox in normalised units so boxes scale with the image, whatever
          // the container width.
          <svg viewBox="0 0 1000 1000" preserveAspectRatio="none"
            className="pointer-events-none absolute inset-0 h-full w-full">
            {overlayBlocks.filter((b) => b.bbox).map((b, i) => {
              const [x1, y1, x2, y2] = b.bbox!;
              return (
                <rect key={i} x={x1} y={y1} width={Math.max(x2 - x1, 1)} height={Math.max(y2 - y1, 1)}
                  fill={colourFor(b.category)} fillOpacity={0.08}
                  stroke={colourFor(b.category)} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              );
            })}
          </svg>
        )}
      </div>
      <div className="flex items-center justify-between text-fh-meta text-fh-text-secondary">
        <div className="flex items-center gap-2">
          <button onClick={() => onGoto(pageNum - 1)} disabled={pageNum <= 1}
            className="rounded px-2 py-1 ring-1 ring-fh-border-soft disabled:opacity-30">←</button>
          <span>Page {pageNum} / {pageCount}</span>
          <button onClick={() => onGoto(pageNum + 1)} disabled={pageNum >= pageCount}
            className="rounded px-2 py-1 ring-1 ring-fh-border-soft disabled:opacity-30">→</button>
        </div>
        <button onClick={() => inputRef.current?.click()} className="hover:text-fh-text-strong">Change PDF</button>
        <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      </div>
    </div>
  );
}
